import { Component, DestroyRef, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Address } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@entities/app/address.entity';
import { AddressResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/address.response';
import { LocationListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/location-list.response';
import { LocalFrostDatesResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/local-frost-dates.response';
import { SharedModule } from '../../../../../@commons/ui-components/shared.module';
import { BaseToastComponent } from '../../../../../@commons/ui-components/toast-popup/base-toast-component';
import { ListRowComponent } from '../../../../../@commons/ui-components/list-row/list-row.component';
import { ListSectionComponent } from '../../../../../@commons/ui-components/list-row/list-section.component';
import { localizedZoneName } from '../../../../../@commons/pipes/zone-name.pipe';
import { dayMonthName } from '../../../../../@commons/@services/front/seed-timing.util';
import { currentAppLang } from '../../../../../@commons/pipes/vw-date.pipe';
import RoutesConstants from '../../../../../@commons/constants/routes.constants';
import { LocationHomeIconComponent } from '../../../../../@icons/location-home-icon.component';
import { AlertCategoryIconComponent } from '../../../../../@icons/alert-category-icon.component';
import { GardenIconComponent } from '../../../../../@icons/garden-icon.component';
import { FrostDatesToastComponent } from '../../../../../calendar/@toasts/frost-dates-toast/frost-dates-toast.component';
import { EditUserAdressToastComponent } from '../edit-user-adress-toast/edit-user-adress-toast.component';
import { AddressLocationsService } from '../../../../address-locations.service';

/**
 * La fiche d'un potager, ouverte depuis le groupe « Mon potager » du Compte :
 * sa commune, puis un groupe par sujet comme le reste du Compte (PC-10).
 *
 * - Adresse : la rue complète ; une adresse que le serveur n'a pas pu situer
 *   (sans coordonnées) le dit, car météo et alertes en dépendent. La
 *   modification ouvre sa propre feuille.
 * - Climat : le climat et, pour l'adresse de l'app, les dates de gel, qui
 *   ouvrent la feuille du calendrier.
 * - Zones de culture : leur nombre et leurs noms ; elles se gèrent dans Mon
 *   potager, où vivent aussi planches et plantes.
 *
 * Climat et zones viennent de la ligne du Compte, qui les a déjà lus : la
 * fiche ne les relit pas. Les feuilles qu'elle ouvre sont posées à côté de la
 * sienne, pas dedans : une feuille décalée par `transform` piégerait leur
 * position fixe.
 */
@Component({
  selector: 'vw-garden-address-toast',
  imports: [
    SharedModule,
    TranslatePipe,
    ListRowComponent,
    ListSectionComponent,
    LocationHomeIconComponent,
    AlertCategoryIconComponent,
    GardenIconComponent,
    FrostDatesToastComponent,
    EditUserAdressToastComponent,
  ],
  templateUrl: './garden-address-toast.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class GardenAddressToastComponent extends BaseToastComponent implements OnChanges {
  /** L'adresse telle que la liste du Compte la donne (commune et adresse complète). */
  @Input() address: Address;
  /** Climat lisible, déjà traduit ; vide tant qu'il n'est pas connu. */
  @Input() climateLabel = '';
  /** Zones de l'adresse ; `null` tant qu'elles ne sont pas chargées. */
  @Input() locations: LocationListResponse[] | null = null;
  /** « 4 zones de culture », déjà traduit. */
  @Input() zonesLabel = '';

  /** L'adresse complète, avec ses coordonnées ; `null` tant qu'elle n'est pas lue. */
  detail: AddressResponse | null = null;
  frostDates: LocalFrostDatesResponse | null = null;
  isEditAddressOpen = false;
  isFrostOpen = false;

  private readonly addressLocations = inject(AddressLocationsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private reading = false;

  /** L'adresse complète et les dates de gel ne se lisent qu'à l'ouverture de la fiche. */
  ngOnChanges(changes: SimpleChanges) {
    if (!changes['isOpen'] || !this.isOpen || this.reading || !this.address?.uid) return;
    this.reading = true;
    const uid = this.address.uid;
    this.addressLocations
      .address$(uid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((detail) => (this.detail = detail));
    this.addressLocations
      .frostDates$(uid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((frost) => (this.frostDates = frost));
  }

  /** La commune d'abord : c'est elle qu'on reconnaît. */
  get placeLabel(): string {
    const a = this.detail ?? this.address;
    return a?.city || a?.line1 || a?.postalCode || '';
  }

  /** « Rue du Fort 3, 4460 Grâce-Hollogne, Belgique ». */
  get addressLine(): string {
    const a = this.detail ?? this.address;
    return a?.fullAddress || a?.address || a?.line1 || a?.city || '';
  }

  /**
   * Faux seulement quand le serveur a rendu l'adresse SANS coordonnées : pas
   * encore située, ou introuvable. Tant qu'elle n'est pas lue, on ne dit rien.
   */
  get isLocated(): boolean {
    const d = this.detail;
    return !d || (d.latitude != null && d.longitude != null);
  }

  /** Mon potager montre l'adresse de l'app : les zones d'une autre adresse ne s'y gèrent pas. */
  get isAppAddress(): boolean {
    return this.addressLocations.isAppAddress(this.address?.uid);
  }

  /** « Jardin, Serre du fond, Intérieur » ; les zones héritées gardent le nom de leur type. */
  get zoneNames(): string {
    return (this.locations ?? []).map((zone) => localizedZoneName(zone, this.translate)).filter(Boolean).join(', ');
  }

  /**
   * « 4 zones de culture », « Aucune zone pour l'instant », ou, zones
   * inconnues, « Voir mes zones » pour l'adresse de l'app et rien ailleurs.
   */
  get zonesTitle(): string {
    if (this.locations) {
      return this.locations.length ? this.zonesLabel : this.translate.instant('profile.garden-sheet.zones.none');
    }
    return this.isAppAddress ? this.translate.instant('profile.garden-sheet.zones.open') : '';
  }

  get zonesSubtitle(): string {
    if (this.locations?.length) return this.zoneNames;
    if (!this.isAppAddress) return '';
    return this.translate.instant(this.locations ? 'profile.garden-sheet.zones.add' : 'profile.garden-sheet.zones.manage');
  }

  /** « Dernières gelées vers le 20 avril, premières vers le 25 octobre », ou vide sans date. */
  get frostSummary(): string {
    const f = this.frostDates;
    if (!f?.lastSpringFrost && !f?.firstAutumnFrost) return '';
    const lang = currentAppLang(this.translate);
    const last = f.lastSpringFrost ? dayMonthName(f.lastSpringFrost, lang) : '';
    const first = f.firstAutumnFrost ? dayMonthName(f.firstAutumnFrost, lang) : '';
    const key = last && first ? 'both' : last ? 'last' : 'first';
    return this.translate.instant('profile.garden-sheet.frost.' + key, { last, first });
  }

  /** Les zones se gèrent dans Mon potager : on y va, la fiche se ferme. */
  openGarden() {
    this.onClose();
    void this.router.navigate([RoutesConstants.GARDEN]);
  }

  /** Adresse enregistrée : la fiche, la ligne du Compte et ses lectures repartent de la nouvelle. */
  onEditAddressClosed(saved?: boolean) {
    this.isEditAddressOpen = false;
    if (saved) this.addressLocations.refresh(this.address.uid);
  }
}
